import { Building2, GraduationCap, Calendar, Users, Megaphone } from 'lucide-react';

export interface PreviewVisibility {
  mostrarLogo: boolean;
  mostrarPrefeito: boolean;
  mostrarSlogan: boolean;
  mostrarData: boolean;
  mostrarDestinatario: boolean;
  mostrarPrograma: boolean;
}

export const DEFAULT_PREVIEW_VISIBILITY: PreviewVisibility = {
  mostrarLogo: true,
  mostrarPrefeito: true,
  mostrarSlogan: true,
  mostrarData: true,
  mostrarDestinatario: true,
  mostrarPrograma: true,
};

interface Props {
  titulo: string;
  corpo: string;
  tipo: string;
  categoria: string;
  destinatario: string;
  midiaUrl?: string;
  branding: {
    tenantName: string;
    tenantBadge?: string;
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    slogan?: string;
    autoridadeNome?: string;
    autoridadeCargo?: string;
  };
  visibility?: PreviewVisibility;
}

const DEST_MAP: Record<string, string> = {
  TODOS: 'Toda a Comunidade',
  DIRETORES_EQUIPE: 'Direção & Equipe',
  PROFESSORES: 'Professores',
  RESPONSAVEIS: 'Responsáveis',
  SERIE: 'Série Específica',
  TURMA: 'Turma Específica',
};

const CAT_THEMES: Record<string, { gradient: string; accent: string }> = {
  OPERACIONAL: { gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', accent: '#60a5fa' },
  INSTITUCIONAL: { gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)', accent: '#a78bfa' },
  PEDAGOGICO: { gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)', accent: '#6ee7b7' },
  TEMPLATE: { gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)', accent: '#6ee7b7' },
};

export function ComunicadoPreviewCore({
  titulo,
  corpo,
  tipo,
  categoria,
  destinatario,
  midiaUrl,
  branding,
  visibility = DEFAULT_PREVIEW_VISIBILITY,
}: Props) {
  const v = visibility;
  const cor1 = branding.primaryColor || '#1e3a5f';
  const cor2 = branding.secondaryColor || '#2563eb';
  const baseTheme = CAT_THEMES[categoria] || CAT_THEMES.OPERACIONAL;

  const gradient = branding.primaryColor
    ? `linear-gradient(135deg, ${cor1} 0%, ${cor2} 100%)`
    : baseTheme.gradient;

  const accent = branding.primaryColor || baseTheme.accent;
  const hoje = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="ccpv" id="comunicado-preview-card">
      {/* Header com Branding */}
      <div className="ccpv-header" style={{ background: gradient }}>
        <div className="ccpv-header-bg-circle c1" />
        <div className="ccpv-header-bg-circle c2" />
        <div className="ccpv-header-content">
          <div className="ccpv-logo-area">
            {v.mostrarLogo && (
              <div
                className="ccpv-logo-circle"
                style={branding.logoUrl ? { padding: 0, overflow: 'hidden' } : undefined}
              >
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt="Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <Building2 size={20} />
                )}
              </div>
            )}
            <div>
              <div className="ccpv-muni-name">{branding.tenantName || 'Instituição de Ensino'}</div>
              <div className="ccpv-muni-sub">{branding.tenantBadge || 'Secretaria / Direção'}</div>
            </div>
          </div>
          <div className="ccpv-tipo-badge" style={{ background: `${accent}30`, color: 'white' }}>
            {tipo || 'COMUNICADO'}
          </div>
        </div>
      </div>

      {/* Se houver imagem anexada (folder), exibe em destaque */}
      {midiaUrl && (
        <div style={{ width: '100%', maxHeight: 200, overflow: 'hidden', background: '#0f172a' }}>
          <img
            src={midiaUrl}
            alt="Folder do Comunicado"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )}

      {/* Corpo */}
      <div className="ccpv-body">
        <h2 className="ccpv-titulo">{titulo || 'Título do comunicado'}</h2>
        <div className="ccpv-divider" style={{ background: accent }} />
        <p className="ccpv-corpo">{corpo || 'Conteúdo do comunicado aparecerá aqui formatado para visualização das famílias...'}</p>

        {/* Metadados */}
        <div className="ccpv-meta-grid">
          {v.mostrarData && (
            <div className="ccpv-meta-item">
              <Calendar size={13} style={{ color: accent }} />
              <span>{hoje}</span>
            </div>
          )}
          {v.mostrarDestinatario && (
            <div className="ccpv-meta-item">
              <Users size={13} style={{ color: accent }} />
              <span>{DEST_MAP[destinatario] || destinatario}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer com Assinatura / Slogan */}
      <div className="ccpv-footer">
        {v.mostrarPrefeito && branding.autoridadeNome && (
          <div className="ccpv-prefeito-info">
            <span className="ccpv-prefeito-nome">{branding.autoridadeNome}</span>
            <span className="ccpv-prefeito-cargo">{branding.autoridadeCargo || 'Gestor Responsável'}</span>
          </div>
        )}
        {v.mostrarSlogan && branding.slogan && (
          <div className="ccpv-slogan">{branding.slogan}</div>
        )}
        {v.mostrarPrograma && (
          <div className="ccpv-powered">
            <GraduationCap size={12} />
            <span>Sistema Aluno Conectado — Gestão Integrada</span>
          </div>
        )}
      </div>
    </div>
  );
}
